import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

interface User {
  id: string
  username: string
  role: string
}

interface AuthContextType {
  user: User | null
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  isLoading: boolean
  isAuthenticated: boolean
  userRole: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  // API 기본 설정 - 상대 경로 사용 (Nginx 프록시를 통해 백엔드로 연결)
  axios.defaults.withCredentials = true

  // 🆕 Axios Interceptor: 401 에러 시 자동 로그아웃 및 로그인 페이지 이동
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response, // 정상 응답은 그대로 반환
      (error) => {
        // 401 Unauthorized 에러 처리
        if (error.response?.status === 401) {
          // 로그인 페이지 요청이 아닌 경우만 처리 (무한 루프 방지)
          const isLoginRequest = error.config?.url?.includes('/login') || 
                                 error.config?.url?.includes('/verify-token')
          
          if (!isLoginRequest) {
            console.warn('세션이 만료되었습니다. 로그인 페이지로 이동합니다.')
            
            // 사용자 상태 초기화
            setUser(null)
            localStorage.removeItem('userRole')
            localStorage.removeItem('username')
            
            // 로그인 페이지로 리다이렉트
            navigate('/login', { 
              state: { 
                message: '세션이 만료되었습니다. 다시 로그인해주세요.',
                from: window.location.pathname 
              } 
            })
          }
        }
        
        return Promise.reject(error)
      }
    )

    // Cleanup: 컴포넌트 언마운트 시 interceptor 제거
    return () => {
      axios.interceptors.response.eject(interceptor)
    }
  }, [navigate])

  // 토큰 검증
  const verifyToken = async () => {
    try {
      const response = await axios.get('/verify-token')
      if (response.data.success) {
        // localStorage에서 username과 role 복원
        const storedUsername = localStorage.getItem('username')
        const storedRole = localStorage.getItem('userRole')
        
        setUser({
          id: storedUsername || response.data.user?.id || 'user',
          username: storedUsername || response.data.user?.username || 'user',
          role: storedRole || response.data.user?.role || 'admin'
        })
      } else {
        // 인증 실패 시 localStorage 정리
        localStorage.removeItem('userRole')
        localStorage.removeItem('username')
      }
    } catch (error) {
      console.log('Token verification failed:', error)
      // 에러 발생 시에도 localStorage 정리
      localStorage.removeItem('userRole')
      localStorage.removeItem('username')
    } finally {
      setIsLoading(false)
    }
  }

  // 로그인
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      const response = await axios.post('/login', { username, password })
      
      if (response.data.success) {
        const role = response.data.role || 'admin'
        setUser({ id: username, username, role })
        
        // localStorage에 role 저장 (페이지별 권한 체크용)
        localStorage.setItem('userRole', role)
        localStorage.setItem('username', username)
        
        navigate('/admin/dashboard')
        return true
      }
      return false
    } catch (error) {
      console.error('Login failed:', error)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  // 로그아웃
  const logout = async () => {
    try {
      await axios.post('/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      // localStorage 정리
      localStorage.removeItem('userRole')
      localStorage.removeItem('username')
      navigate('/login')
    }
  }

  useEffect(() => {
    verifyToken()
  }, [])

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user,
    userRole: user?.role || null,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}






